
import Vue from "vue";
import { getTalentMembershipHistory } from "@/api";
import { MembershipHistoryEntry } from "@/interfaces/MembershipHistory";
import { getFfsStatusLabel } from "@/helpers/ffsStatus";

const easternTime = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Toronto",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  timeZoneName: "short"
});

export default Vue.extend({
  name: "MembershipStatusHistory",
  props: {
    hygienistId: { type: Number, required: true },
    active: { type: Boolean, default: true },
    context: { type: String, default: "profile" }
  },
  data() {
    return {
      entries: [] as MembershipHistoryEntry[],
      page: 0,
      hasMore: false,
      loading: false,
      error: false,
      requestVersion: 0
    };
  },
  watch: {
    hygienistId: {
      immediate: true,
      handler() {
        this.resetHistory();
      }
    },
    active() {
      this.resetHistory();
    }
  },
  beforeDestroy() {
    this.requestVersion += 1;
  },
  methods: {
    resetHistory() {
      // Invalidate in-flight reads before a different Talent or view can use them.
      this.requestVersion += 1;
      this.entries = [];
      this.page = 0;
      this.hasMore = false;
      this.loading = false;
      this.error = false;
      if (
        this.active &&
        Number.isSafeInteger(this.hygienistId) &&
        this.hygienistId > 0
      ) {
        this.loadNextPage();
      }
    },
    async loadNextPage(): Promise<void> {
      if (this.loading || !this.active) return;
      const requestVersion = ++this.requestVersion;
      const hygienistId = this.hygienistId;
      this.loading = true;
      this.error = false;
      try {
        const result = await getTalentMembershipHistory(
          hygienistId,
          this.page + 1
        );
        if (
          requestVersion !== this.requestVersion ||
          hygienistId !== this.hygienistId
        )
          return;
        // A new head entry can shift offset pages between reads. Keep each
        // canonical event once without changing any persisted note or history.
        const unique = new Map(this.entries.map(entry => [entry.id, entry]));
        result.items.forEach(entry => unique.set(entry.id, entry));
        this.entries = Array.from(unique.values()).sort(
          (left, right) =>
            (Date.parse(right.changedOn || "") || 0) -
              (Date.parse(left.changedOn || "") || 0) || right.id - left.id
        );
        this.page = result.page;
        this.hasMore = result.hasMore;
      } catch (_) {
        if (
          requestVersion === this.requestVersion &&
          hygienistId === this.hygienistId
        ) {
          this.error = true;
        }
      } finally {
        if (requestVersion === this.requestVersion) this.loading = false;
      }
    },
    statusLabel(code: string | null): string {
      return code ? getFfsStatusLabel(code) : "Not recorded";
    },
    formatTime(timestamp: string | null): string {
      const date = timestamp ? new Date(timestamp) : null;
      return date && Number.isFinite(date.getTime())
        ? easternTime.format(date)
        : "Date not recorded";
    }
  }
});
